import React, { useState, useEffect } from "react";
import {
    IonBackButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonItem,
    IonList,
    IonPage,
    IonTitle,
    IonToolbar,
    IonText,
    IonSpinner,
    IonToast,
} from "@ionic/react";
import { useTranslation } from "react-i18next";
import { getDatabase } from "../../services/database/fitnessDatabases.service";
import { FitnessDataType, FitnessData } from "refsix-js-models";
import { runSync } from "../../services/fitnessDataSync/fitnessDataSyncService";
import config from "../../services/fitnessDataSync/config";
import FitnessDataItem from "../../components/FitnessDataItem";

interface MatchFitnessData {
    matchId: string;
    gpsData?: FitnessData;
    heartRateData?: FitnessData;
    gpsSynced: boolean;
    heartRateSynced: boolean;
}

interface SyncingState {
    [matchId: string]: {
        gps: boolean;
        heartRate: boolean;
    };
}

const FitnessDataStatus: React.FC = () => {
    const { t } = useTranslation();
    const [matchData, setMatchData] = useState<MatchFitnessData[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [syncing, setSyncing] = useState<SyncingState>({});
    const [toast, setToast] = useState<{
        message: string;
        color: string;
    } | null>(null);

    useEffect(() => {
        loadFitnessData();
    }, []);

    const loadFitnessData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Get both GPS and HeartRate databases
            const gpsDb = getDatabase(FitnessDataType.GPS);
            const heartRateDb = getDatabase(FitnessDataType.HeartRate);

            // Get all documents from both databases
            const [gpsResult, heartRateResult] = await Promise.all([
                gpsDb.allDocs({ include_docs: true }),
                heartRateDb.allDocs({ include_docs: true }),
            ]);

            // Create a map to combine data by match ID
            const matchDataMap = new Map<string, MatchFitnessData>();

            // Process GPS data
            gpsResult.rows.forEach((row) => {
                if (row.doc) {
                    const matchId = row.doc._id;
                    if (!matchDataMap.has(matchId)) {
                        matchDataMap.set(matchId, {
                            matchId,
                            gpsSynced: false,
                            heartRateSynced: false,
                        });
                    }
                    const matchData = matchDataMap.get(matchId)!;
                    matchData.gpsData = row.doc;
                    matchData.gpsSynced = (row.doc as any).synced || false;
                }
            });

            // Process HeartRate data
            heartRateResult.rows.forEach((row) => {
                if (row.doc) {
                    const matchId = row.doc._id;
                    if (!matchDataMap.has(matchId)) {
                        matchDataMap.set(matchId, {
                            matchId,
                            gpsSynced: false,
                            heartRateSynced: false,
                        });
                    }
                    const matchData = matchDataMap.get(matchId)!;
                    matchData.heartRateData = row.doc;
                    matchData.heartRateSynced =
                        (row.doc as any).synced || false;
                }
            });

            // Convert to array and sort by match ID
            const matchDataArray = Array.from(matchDataMap.values()).sort(
                (a, b) => b.matchId.localeCompare(a.matchId)
            );

            setMatchData(matchDataArray);
        } catch (err) {
            console.error("Error loading fitness data:", err);
            setError("Failed to load fitness data");
        } finally {
            setLoading(false);
        }
    };

    const syncMatchData = async (
        matchId: string,
        dataType: FitnessDataType
    ) => {
        try {
            // Set syncing state
            setSyncing((prev) => ({
                ...prev,
                [matchId]: {
                    ...prev[matchId],
                    [dataType === FitnessDataType.GPS ? "gps" : "heartRate"]:
                        true,
                },
            }));

            const database = getDatabase(dataType);
            const dataConfig = config[dataType];

            // Mark data as unsynced to trigger sync
            await database.upsert(
                matchId,
                (doc) => ({ ...doc, synced: false } as any)
            );

            // Run sync for this specific data
            await runSync(database, dataConfig);

            // Reload data to reflect changes
            loadFitnessData();

            setToast({
                message: `${
                    dataType === FitnessDataType.GPS ? "GPS" : "Heart Rate"
                } data synced successfully`,
                color: "success",
            });
        } catch (err) {
            console.error(`Error syncing ${dataType} data:`, err);
            setToast({
                message: `Failed to sync ${
                    dataType === FitnessDataType.GPS ? "GPS" : "Heart Rate"
                } data`,
                color: "danger",
            });
        } finally {
            // Clear syncing state
            setSyncing((prev) => ({
                ...prev,
                [matchId]: {
                    ...prev[matchId],
                    [dataType === FitnessDataType.GPS ? "gps" : "heartRate"]:
                        false,
                },
            }));
        }
    };


    if (loading) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonBackButton
                                defaultHref="/developer-tools"
                                text={t("help.OnboardingVideo.controls.back")}
                            />
                        </IonButtons>
                        <IonTitle>Fitness Data Status</IonTitle>
                    </IonToolbar>
                </IonHeader>
                <IonContent className="ion-padding">
                    <div style={{ textAlign: "center", marginTop: "2rem" }}>
                        <IonSpinner />
                        <p>Loading fitness data...</p>
                    </div>
                </IonContent>
            </IonPage>
        );
    }

    if (error) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonBackButton
                                defaultHref="/developer-tools"
                                text={t("help.OnboardingVideo.controls.back")}
                            />
                        </IonButtons>
                        <IonTitle>Fitness Data Status</IonTitle>
                    </IonToolbar>
                </IonHeader>
                <IonContent className="ion-padding">
                    <IonText color="danger">
                        <h2>Error</h2>
                        <p>{error}</p>
                    </IonText>
                </IonContent>
            </IonPage>
        );
    }

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonBackButton
                            defaultHref="/developer-tools"
                            text={t("help.OnboardingVideo.controls.back")}
                        />
                    </IonButtons>
                    <IonTitle>Fitness Data Status</IonTitle>
                </IonToolbar>
            </IonHeader>
            <IonContent>
                <IonList>
                    <IonItem>
                        <IonText>
                            <p>
                                This page shows all matches that have fitness
                                data stored locally. Green badges indicate
                                synced data, orange badges indicate unsynced
                                data.
                            </p>
                        </IonText>
                    </IonItem>

                    {matchData.length === 0 ? (
                        <IonItem>
                            <IonText color="medium">
                                No fitness data found in local storage.
                            </IonText>
                        </IonItem>
                    ) : (
                        matchData.map((fitnessData) => (
                            <FitnessDataItem
                                key={fitnessData.matchId}
                                fitnessData={fitnessData}
                                syncing={syncing}
                                onSync={syncMatchData}
                                showDebugInfo={true}
                            />
                        ))
                    )}
                </IonList>
                <IonToast
                    isOpen={!!toast}
                    onDidDismiss={() => setToast(null)}
                    message={toast?.message}
                    duration={3000}
                    color={toast?.color}
                />
            </IonContent>
        </IonPage>
    );
};

export default FitnessDataStatus;
